import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getGiftCardCatalog, saveGiftCardDesign, isOk, messageOf } from "../../../services/gift";
import GiftCard from "./GiftCard";

const STEPS = ["Intention", "Occasion", "Wording", "Personalize"];

const DEFAULT_COLOR_ID = "sage";

// The catalog's authored wording for an option, with or without the name.
const linesFor = (option, name) => [
  option.line1,
  option.line2_with_name && name ? option.line2_with_name.replace("[Name]", name) : option.line2,
  option.line3,
];

const findOption = (catalog, optionId) => {
  for (let c = 0; c < catalog.categories.length; c++) {
    const category = catalog.categories[c];
    for (let o = 0; o < category.occasions.length; o++) {
      const occasion = category.occasions[o];
      for (let i = 0; i < occasion.options.length; i++) {
        if (occasion.options[i].id === optionId) {
          return { category, occasion, option: occasion.options[i] };
        }
      }
    }
  }
  return null;
};

// "Personalize your card": Intention -> Occasion -> Wording -> Personalize,
// with the card updating live beside the choices. Only the words are saved;
// the code, amount and expiry always come from the gift itself.
export default function GiftCardDesigner({ gift, onSaved }) {
  const design = gift.design || {};
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [step, setStep] = useState(1);
  const [categoryId, setCategoryId] = useState(null);
  const [occasionId, setOccasionId] = useState(null);
  const [optionId, setOptionId] = useState(design.option_id || null);
  const [custom, setCustom] = useState(!!design.custom);
  const [customLines, setCustomLines] = useState({ line1: "", line2: "", line3: "" });
  const [name, setName] = useState(design.name || "");
  const [fromName, setFromName] = useState(design.from_name || "");
  const [colorId, setColorId] = useState((design.color && design.color.id) || DEFAULT_COLOR_ID);
  const [saving, setSaving] = useState(false);

  // Load the catalog the first time the panel opens, and start from the saved choice.
  useEffect(() => {
    if (!open || catalog) return;
    getGiftCardCatalog().then((res) => {
      if (!isOk(res)) {
        toast.error(messageOf(res, "The card wordings could not be loaded."));
        setOpen(false);
        return;
      }
      const loaded = res.data.data;
      setCatalog(loaded);
      const found = findOption(loaded, design.option_id || loaded.categories[0].occasions[0].options[0].id);
      if (found) {
        setCategoryId(found.category.id);
        setOccasionId(found.occasion.id);
        if (!design.custom) setOptionId(found.option.id);
      }
      if (design.custom) {
        const lines = gift.design.lines || [];
        setCustomLines({ line1: lines[0] || "", line2: lines[1] || "", line3: lines[2] || "" });
      } else {
        setCustomLines({ line1: "", line2: "", line3: loaded.experience_line });
      }
    });
  }, [open]);

  if (!open) {
    return (
      <div className="vsr-card vsr-card-panel vsr-designer-closed no-print">
        <div>
          <h2 className="vsr-designer-title">Personalize your card</h2>
          <p className="vsr-muted">Choose the occasion and wording your recipient will read.</p>
        </div>
        <button type="button" className="vsr-btn vsr-btn-outline" onClick={() => setOpen(true)}>
          Personalize card
        </button>
      </div>
    );
  }

  if (!catalog) {
    return <div className="vsr-card vsr-card-panel no-print">Loading…</div>;
  }

  const category = catalog.categories.find((item) => item.id === categoryId) || catalog.categories[0];
  const occasion = category.occasions.find((item) => item.id === occasionId) || category.occasions[0];
  const picked = !custom && optionId ? findOption(catalog, optionId) : null;
  const usesName = !!(picked && picked.option.line2_with_name);
  const limits = catalog.limits;
  const color = catalog.palette.find((item) => item.id === colorId) || catalog.palette[0];

  const previewLines = custom
    ? [customLines.line1, customLines.line2, customLines.line3].filter((line) => line.trim() !== "")
    : picked
    ? linesFor(picked.option, name.trim())
    : gift.design.lines;

  const chooseCategory = (item) => {
    setCategoryId(item.id);
    setOccasionId(item.occasions[0].id);
    setStep(2);
  };

  const chooseOccasion = (item) => {
    setOccasionId(item.id);
    setCustom(false);
    setOptionId(item.options[0].id);
    setStep(3);
  };

  const chooseOption = (id) => {
    setCustom(false);
    setOptionId(id);
  };

  const save = async () => {
    const body = custom
      ? { custom: customLines, from_name: fromName.trim(), color: colorId }
      : { option_id: optionId, name: usesName ? name.trim() : "", from_name: fromName.trim(), color: colorId };
    setSaving(true);
    const res = await saveGiftCardDesign(gift.gift_id, body);
    setSaving(false);
    if (isOk(res)) {
      toast.success(messageOf(res, "Your card has been updated."));
      onSaved(res.data.data);
      setOpen(false);
      setStep(1);
    } else {
      toast.error(messageOf(res, "Your card could not be saved."));
    }
  };

  return (
    <div className="vsr-card vsr-card-panel vsr-designer no-print">
      <h2 className="vsr-designer-title">Personalize your card</h2>
      <ol className="vsr-designer-steps" aria-label="Steps">
        {STEPS.map((label, index) => (
          <li key={label} className={step === index + 1 ? "is-current" : step > index + 1 ? "is-done" : ""}>
            <button type="button" onClick={() => setStep(index + 1)} aria-current={step === index + 1 ? "step" : undefined}>
              {index + 1}. {label}
            </button>
          </li>
        ))}
      </ol>

      <div className="vsr-designer-body">
        <div className="vsr-designer-controls">
          {step === 1 && (
            <div className="vsr-designer-list" role="radiogroup" aria-label="Intention">
              {catalog.categories.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`vsr-designer-option ${item.id === category.id ? "is-selected" : ""}`}
                  onClick={() => chooseCategory(item)}
                >
                  <b>{item.title}</b>
                  <span>{item.description}</span>
                </button>
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="vsr-designer-chips" role="radiogroup" aria-label="Occasion">
              {category.occasions.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`vsr-designer-chip ${item.id === occasion.id && !custom ? "is-selected" : ""}`}
                  onClick={() => chooseOccasion(item)}
                >
                  {item.title}
                </button>
              ))}
            </div>
          )}

          {step === 3 && (
            <div className="vsr-designer-list" role="radiogroup" aria-label="Wording">
              {occasion.options.map((option) => {
                const lines = linesFor(option, name.trim());
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={`vsr-designer-option ${!custom && optionId === option.id ? "is-selected" : ""}`}
                    onClick={() => chooseOption(option.id)}
                  >
                    {option.style && <em>{option.style}</em>}
                    <b>{lines[0]}</b>
                    <span>{lines[1]}</span>
                    <span>{lines[2]}</span>
                  </button>
                );
              })}
              <button
                type="button"
                className={`vsr-designer-option ${custom ? "is-selected" : ""}`}
                onClick={() => setCustom(true)}
              >
                <b>Write my own</b>
                <span>Use your own words on the card.</span>
              </button>
            </div>
          )}

          {step === 4 && (
            <div className="vsr-designer-fields">
              {custom ? (
                [
                  ["line1", "Heading", limits.line1],
                  ["line2", "Your message", limits.line2],
                  ["line3", "Invitation", limits.line3],
                ].map((row) => (
                  <div className="vsr-field" key={row[0]}>
                    <label className="vsr-label" htmlFor={`card-${row[0]}`}>
                      {row[1]}
                    </label>
                    <input
                      id={`card-${row[0]}`}
                      className="vsr-input"
                      type="text"
                      maxLength={row[2]}
                      value={customLines[row[0]]}
                      onChange={(e) => setCustomLines({ ...customLines, [row[0]]: e.target.value })}
                    />
                    <span className="vsr-hint">
                      {customLines[row[0]].length} / {row[2]}
                    </span>
                  </div>
                ))
              ) : usesName ? (
                <div className="vsr-field">
                  <label className="vsr-label" htmlFor="card-name">
                    Their name (optional)
                  </label>
                  <input
                    id="card-name"
                    className="vsr-input"
                    type="text"
                    maxLength={limits.name}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Leave empty to keep the card without a name"
                  />
                  <span className="vsr-hint">This wording reads naturally with or without a name.</span>
                </div>
              ) : (
                <p className="vsr-muted">This wording doesn't use a name, so there's nothing to fill in here.</p>
              )}
              <div className="vsr-field">
                <label className="vsr-label" htmlFor="card-from">
                  From (optional)
                </label>
                <input
                  id="card-from"
                  className="vsr-input"
                  type="text"
                  maxLength={limits.name}
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                  placeholder="Your name, if you'd like it on the card"
                />
                <span className="vsr-hint">Leave empty to keep the gift anonymous.</span>
              </div>
              <div className="vsr-field">
                <span className="vsr-label" id="card-color-label">
                  Card color
                </span>
                <div className="vs-swatches" role="radiogroup" aria-labelledby="card-color-label">
                  {catalog.palette.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      role="radio"
                      aria-checked={item.id === colorId}
                      aria-label={item.title}
                      title={item.title}
                      className={`vs-swatch ${item.id === colorId ? "is-selected" : ""}`}
                      style={{ background: item.hex }}
                      onClick={() => setColorId(item.id)}
                    />
                  ))}
                </div>
                <span className="vsr-hint">{color.title}</span>
              </div>
            </div>
          )}

          <div className="vsr-designer-nav">
            {step > 1 && (
              <button type="button" className="vsr-btn vsr-btn-outline vsr-btn-sm" onClick={() => setStep(step - 1)}>
                Back
              </button>
            )}
            {step === 3 && (
              <button type="button" className="vsr-btn vsr-btn-sm" onClick={() => setStep(4)}>
                Next
              </button>
            )}
            {step === 4 && (
              <button type="button" className="vsr-btn vsr-btn-sm" disabled={saving} onClick={save}>
                {saving ? "Saving…" : "Save card"}
              </button>
            )}
            <button type="button" className="vsr-link" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </div>

        <div className="vsr-designer-preview" aria-live="polite">
          <p className="vsr-muted">Preview</p>
          <GiftCard
            code={gift.code}
            qrSvg={gift.qr_svg}
            redeemUrl={gift.redeem_url}
            worthUpTo={gift.worth_up_to}
            expiresOn={gift.expires_on}
            lines={previewLines}
            fromName={fromName.trim()}
            color={color.hex}
          />
        </div>
      </div>
    </div>
  );
}
